import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAutocomplete,
  MiChip,
  MiIcon,
  MiPopper,
} from "@miplatform/components";
import { STANDARD_DATE_FORMAT } from "@miplatform/constants";
import {
  CalendarEventType,
  IAttemptListItemResponseForWorkOrder,
} from "@miplatform/types";
import { getContrastColor, getDate } from "@miplatform/utils";
import { mdiLoading, mdiMagnify } from "@mdi/js";
import {
  Box,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { MiWorkOrderTooltip } from "../../../components/MiWorkOrderCard/MiWorkOrderTooltip";
import { useCalendarContext } from "../../../providers";
import { useCalendarAttemptSearchViewModel } from "./useCalendarAttemptSearchViewModel";

export const CalendarAttemptSearch = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hoveredAttempt, setHoveredAttempt] =
    useState<null | IAttemptListItemResponseForWorkOrder>(null);

  const { assigneeModeUser, calendarConfig, calendarSet, setSelectedAttempt } =
    useCalendarContext();

  const {
    attempts,
    search,
    isLoading,
    debouncedSearch,
    setSearch,
    getAttempts,
  } = useCalendarAttemptSearchViewModel({
    eventType: calendarSet?.config.eventType || CalendarEventType.WorkOrder,
    workTemplateBases: calendarConfig?.workTemplateBases || [],
    assigneeModeUser,
  });

  useEffect(() => {
    if (debouncedSearch) {
      getAttempts();
    }
  }, [debouncedSearch]);

  const { classes, theme } = useStyles();

  const handleClear = () => {
    setSearch("");
  };

  const handleSelect = (
    attempt: IAttemptListItemResponseForWorkOrder | null
  ) => {
    setSelectedAttempt(attempt);
    setSearch("");
    handlePopperClose();
  };

  const handlePopperOpen = () => {
    setAnchorEl(document.getElementById(hoveredAttempt?.id || ""));
  };

  const handlePopperClose = () => {
    setAnchorEl(null);
    setHoveredAttempt(null);
  };

  useEffect(() => {
    if (hoveredAttempt?.id) {
      handlePopperOpen();
    }
  }, [hoveredAttempt]);

  return (
    <Box className={classes.root}>
      <MiAutocomplete
        blurOnSelect
        openOnFocus
        options={attempts}
        loading={isLoading}
        inputValue={search}
        onInputChange={(_, value) => {
          setSearch(value.trimStart());
        }}
        onChange={(_, value) => {
          handleSelect(value);
        }}
        getOptionLabel={(option) => option.home?.address?.addressLine1}
        handleClear={handleClear}
        forcePopupIcon={false}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              fullWidth
              placeholder="Search"
              size="small"
              slotProps={{
                input: {
                  ...params.InputProps,
                  style: {
                    padding: theme.spacing(0.5),
                  },
                  inputProps: {
                    "data-testid": "attempt-search-input",
                    ...params.inputProps,
                  },
                  startAdornment: (
                    <InputAdornment position="end">
                      <MiIcon path={mdiMagnify} />
                    </InputAdornment>
                  ),
                  endAdornment: isLoading ? (
                    <InputAdornment position="end">
                      <MiIcon
                        path={mdiLoading}
                        spin={0.75}
                        color={theme.palette.blue.main}
                      />
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          );
        }}
        renderOption={(props, option) => {
          return (
            <MenuItem
              {...props}
              key={option.id}
              id={option.id}
              className={classes.menuItem}
              onMouseEnter={() => {
                setHoveredAttempt(option);
              }}
              onMouseLeave={handlePopperClose}
            >
              <Box display="flex" alignItems="center" gap={2} flex={1}>
                <MiChip
                  className={classes.chip}
                  style={{
                    backgroundColor:
                      option.assignment?.workOrder?.workOrderTemplateColor,
                    color: getContrastColor(
                      option.assignment?.workOrder?.workOrderTemplateColor
                    ),
                  }}
                  label={
                    option.assignment.workOrder?.abbreviation ||
                    option.assignment.workOrder?.workOrderTemplateName?.slice(
                      0,
                      3
                    ) ||
                    ""
                  }
                  size="small"
                />
                <Typography noWrap maxWidth={150}>
                  {option.home?.address?.addressLine1}
                </Typography>
                <Typography>#{option.attemptNumber}</Typography>
              </Box>
              <Typography
                color="grey.medium"
                fontSize={theme.fontSize.medium}
                alignSelf="flex-end"
              >
                {option.scheduledStartDate
                  ? getDate(option.scheduledStartDate).format(
                      STANDARD_DATE_FORMAT
                    )
                  : "--/--/----"}
              </Typography>
            </MenuItem>
          );
        }}
      />
      {hoveredAttempt && anchorEl && (
        <MiPopper
          open
          anchorEl={anchorEl}
          showArrow
          arrowColor={
            hoveredAttempt.assignment?.workOrder?.workOrderTemplateColor
          }
        >
          <MiWorkOrderTooltip attempt={hoveredAttempt} />
        </MiPopper>
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: { width: 400 },
  menuItem: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  chip: {
    fontSize: theme.fontSize.xsmall,
    borderRadius: theme.spacing(1),
    height: 20,
    minWidth: 22,
  },
}));
